"use client";

import { Slot, Slottable } from "@radix-ui/react-slot";
import { useObjectRef } from "@react-aria/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, MouseEvent, Ref } from "react";

import { cn } from "../../utils/cn.util";
import type { SvgIcon } from "../icons/type";
import { iconSizeVariants, LoadingSlot, LoadingSwap } from "./Loading";

export const buttonVariants = cva(
  "flex items-center justify-center gap-2 rounded-lg font-semibold whitespace-nowrap transition duration-100 ease-linear focus-visible:outline-hidden disabled:cursor-not-allowed data-loading:cursor-wait data-placeholder:text-neutral-70",
  {
    variants: {
      color: {
        primary:
          "focus-visible:shadow-button-primary data-[state=open]:border-primary-80 data-[state=open]:shadow-button-primary",
        secondary: "focus-visible:shadow-button-secondary data-[state=open]:shadow-button-secondary",
        error: "focus-visible:shadow-button-error data-[state=open]:shadow-button-error",
        warning: "focus-visible:shadow-button-warning data-[state=open]:shadow-button-warning",
      },
      variant: {
        blank:
          "h-fit! bg-transparent p-0! shadow-none! disabled:text-neutral-50 data-placeholder:text-neutral-70 disabled:data-placeholder:text-neutral-80",
        filled: "",
        light: "",
        outline: "border",
        ghost: "bg-transparent",
        link: "bg-transparent p-0!",
        select: "border bg-white",
      },
      size: {
        input: "h-11 px-3 text-sm font-normal",
        "3xs": "h-5 px-2 text-xs font-normal",
        xxs: "h-5 px-2 text-xs font-normal",
        xs: "h-8 px-2.5 text-xs font-normal",
        sm: "h-9 px-3.5 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-[18px] text-base",
        xl: "h-12 px-5 text-base",
        "2xl": "h-14 px-7 text-base",
      },
    },
    defaultVariants: {
      color: "primary",
      variant: "filled",
      size: "sm",
    },
    compoundVariants: [
      {
        variant: "link",
        size: ["xs", "sm", "md", "lg", "xl", "2xl"],
        className: "h-[unset] focus-visible:ring-0",
      },
      {
        color: "primary",
        variant: "filled",
        className: [
          "bg-primary-50 text-white",
          "enabled:hover:bg-primary-40 data-loading:bg-primary-40",
          "disabled:bg-primary-90",
        ],
      },
      {
        color: "secondary",
        variant: "filled",
        className: [],
      },
      {
        color: "error",
        variant: "filled",
        className: [
          "bg-error-50 text-white",
          "enabled:hover:bg-error-40 data-loading:bg-error-40",
          "disabled:bg-error-90",
        ],
      },
      {
        color: "primary",
        variant: "light",
        className: [
          "bg-primary-95 text-primary-40",
          "enabled:hover:bg-primary-90 data-loading:bg-primary-90",
          "disabled:bg-primary-98 disabled:text-primary-80",
        ],
      },
      {
        color: "secondary",
        variant: "light",
        className: [],
      },
      {
        color: "error",
        variant: "light",
        className: [
          "bg-error-95 text-error-50",
          "enabled:hover:bg-error-90 data-loading:bg-error-90",
          "disabled:bg-error-95 disabled:text-error-80",
        ],
      },
      {
        color: "primary",
        variant: "outline",
        className: [
          "border-neutral-80 bg-white text-neutral-30",
          "enabled:hover:bg-neutral-95 data-loading:bg-neutral-95",
          "disabled:border-neutral-90 disabled:text-neutral-80",
          "focus-visible:shadow-button-secondary data-[state=open]:shadow-button-secondary",
        ],
      },
      {
        color: "secondary",
        variant: "outline",
        className: [
          "border-neutral-80 bg-white text-neutral-30 shadow-xs",
          "enabled:hover:bg-neutral-95 data-loading:bg-neutral-95",
          "disabled:border-neutral-90 disabled:text-neutral-80",
        ],
      },
      {
        color: "error",
        variant: "outline",
        className: [
          "border-error-60 text-error-50",
          "enabled:hover:bg-error-95 data-loading:bg-error-95",
          "disabled:border-error-90 disabled:text-error-90",
        ],
      },
      {
        color: "primary",
        variant: "select",
        // `focus:` mirrors `focus-visible:` so a select trigger also shows its focus border on programmatic
        // focus (not just keyboard), e.g. when a field is focused by script.
        className: [
          "border-neutral-80 bg-white text-neutral-10 focus:border-primary-80 focus:shadow-button-primary focus-visible:border-primary-80",
          "disabled:cursor-not-allowed disabled:bg-neutral-95 disabled:text-neutral-50 data-placeholder:text-neutral-70 disabled:data-placeholder:text-neutral-70",
        ],
      },
      {
        color: "secondary",
        variant: "select",
        className: [
          "border-neutral-80 bg-white text-neutral-10 focus:border-neutral-80 focus:shadow-button-primary focus-visible:border-neutral-80",
          "disabled:cursor-not-allowed disabled:bg-neutral-95 disabled:text-neutral-50 data-placeholder:text-neutral-70 disabled:data-placeholder:text-neutral-70",
        ],
      },
      {
        color: "error",
        variant: "select",
        className: [
          "border-error-50 bg-white text-neutral-10 focus:shadow-button-error",
          "disabled:cursor-not-allowed disabled:bg-neutral-95 disabled:text-neutral-50 data-placeholder:text-neutral-70 disabled:data-placeholder:text-neutral-70",
        ],
      },
      {
        color: "warning",
        variant: "select",
        className: [
          "border-warning-70 bg-white text-neutral-10 focus:shadow-button-warning",
          "disabled:cursor-not-allowed disabled:bg-neutral-95 disabled:text-neutral-50 data-placeholder:text-neutral-70 disabled:data-placeholder:text-neutral-70",
        ],
      },
      {
        color: "primary",
        variant: "ghost",
        className: [
          "text-primary-40",
          "enabled:hover:bg-primary-95 data-loading:bg-primary-95",
          "disabled:text-neutral-80",
        ],
      },
      {
        color: "secondary",
        variant: "ghost",
        className: [
          "text-neutral-50",
          "enabled:hover:bg-neutral-95 data-loading:bg-neutral-95",
          "disabled:text-neutral-80",
          "focus-visible:ring-0",
        ],
      },
      {
        color: "error",
        variant: "ghost",
        className: ["text-error-50", "enabled:hover:bg-error-95 data-loading:bg-error-95", "disabled:text-error-80"],
      },
      {
        color: "primary",
        variant: "link",
        className: [
          "text-primary-50",
          "enabled:hover:text-primary-20 data-loading:text-primary-20",
          "disabled:text-neutral-80",
        ],
      },
      {
        color: "secondary",
        variant: "link",
        className: [
          "text-neutral-50",
          "enabled:hover:text-neutral-40 data-loading:text-neutral-40",
          "disabled:text-neutral-80",
        ],
      },
      {
        color: "error",
        variant: "link",
        className: [
          "text-error-50",
          "enabled:hover:text-error-40 data-loading:text-error-40",
          "disabled:text-error-80",
        ],
      },
    ],
  }
);

const iconButtonVariants = cva("shrink-0", {
  variants: {
    size: {
      input: "",
      "3xs": "h-6 w-6 p-0",
      xxs: "h-7 w-7 p-0",
      xs: "h-8 w-8 p-0",
      sm: "h-9 w-9 p-0",
      md: "h-10 w-10 p-0",
      lg: "h-11 w-11 p-0",
      xl: "h-12 w-12 p-0",
      "2xl": "h-14 w-14 p-0",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

/** Button props without the icon slots: for wrappers that render their own content or slots. */
export type ButtonBaseProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    ref?: Ref<HTMLButtonElement>;
    isInvalid?: boolean;
    isWarning?: boolean;
    isLoading?: boolean;
  };

type ButtonSlotProps =
  | {
      icon: true;
      iconLeading?: never;
      iconTrailing?: never;
    }
  | {
      icon?: false;
      iconLeading?: SvgIcon;
      iconTrailing?: never;
    }
  | {
      icon?: false;
      iconLeading?: never;
      iconTrailing?: SvgIcon;
    };

export type ButtonProps = ButtonBaseProps & ButtonSlotProps;

// A loading button stays focusable, so it can't lean on native `disabled`: like react-aria's `isPending`, drop every
// interaction handler but focus/blur, and cancel the click itself (Enter/Space, implicit form submit, `asChild` links).
const withoutInteractions = <T extends object>(props: T) =>
  Object.fromEntries(Object.entries(props).filter(([key]) => !/^on[A-Z]/.test(key) || /Focus|Blur/.test(key))) as T;

const preventClick = (event: MouseEvent<HTMLButtonElement>) => event.preventDefault();

/** The button's own props, plus the ARIA state and interaction blocking of `isLoading`. */
const getLoadingProps = <T extends object>(props: T, isLoading: boolean, disabled?: boolean) => ({
  "aria-disabled": isLoading || undefined,
  "aria-busy": isLoading || undefined,
  // Styling hook for the loading look (hover color, wait cursor); a disabled button keeps its disabled look.
  "data-loading": (isLoading && !disabled) || undefined,
  ...(isLoading ? { ...withoutInteractions(props), onClick: preventClick } : props),
});

const resolveColor = ({ color, isInvalid, isWarning }: Pick<ButtonProps, "color" | "isInvalid" | "isWarning">) => {
  if (isInvalid) {
    return "error";
  }
  if (isWarning) {
    return "warning";
  }
  return color;
};

const Button = ({
  ref: forwardedRef,
  className,
  color,
  size,
  variant,
  asChild = false,
  icon = false,
  isInvalid,
  isWarning,
  isLoading = false,
  iconLeading: IconLeading,
  iconTrailing: IconTrailing,
  disabled,
  children,
  ...props
}: ButtonProps) => {
  const ref = useObjectRef(forwardedRef);
  const Comp = asChild ? Slot : "button";

  // The spinner replaces an icon button's icon, else `iconLeading` or `iconTrailing` (exclusive), else it gets its own
  // slot before the label.
  return (
    <Comp
      className={cn(
        buttonVariants({
          color: resolveColor({ color, isInvalid, isWarning }),
          size,
          variant,
        }),
        icon && iconButtonVariants({ size }),
        icon && asChild && iconSizeVariants({ size, icon }),
        className
      )}
      ref={ref}
      disabled={disabled}
      {...getLoadingProps(props, isLoading, disabled)}
    >
      {!icon && !IconLeading && !IconTrailing && <LoadingSlot isLoading={isLoading} size={size} />}

      {IconLeading && (
        <LoadingSwap isLoading={isLoading} size={size}>
          <IconLeading />
        </LoadingSwap>
      )}

      {/* `Slottable` lets the slots land inside the `asChild` element instead of breaking `Slot`. */}
      <Slottable>
        {icon && !asChild ? (
          <LoadingSwap isLoading={isLoading} size={size} icon>
            {children}
          </LoadingSwap>
        ) : (
          children
        )}
      </Slottable>

      {IconTrailing && (
        <LoadingSwap isLoading={isLoading} size={size}>
          <IconTrailing />
        </LoadingSwap>
      )}
    </Comp>
  );
};

Button.displayName = "Button";

export default Button;
